'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { getFeatureFlags } from '@sola/config/lib/getters.ts';
import type { ConsentBannerData, ConsentBannerUIProps } from '../../lib/consent-banner-data.ts';
import { oneTrustConfig } from '../../lib/consent-banner-data.ts';
import { ConsentBanner } from './consent-banner.tsx';

/**
 * Static fallback banner data rendered into the initial HTML payload so
 * the banner is part of the LCP paint. Region-specific data replaces it
 * once the client fetches `/api/consent/banner-data` after mount.
 *
 * `showBanner: true` is intentional: the vast majority of our traffic is
 * US (where the banner is required), so showing it immediately is the
 * right tradeoff for LCP. Visitors in regions where OneTrust returns
 * `showBanner: false` will see the banner flash in and disappear once the
 * region-specific fetch resolves. If that flash becomes a concern, flip
 * this to `false` — US users will then see a small delay before the
 * banner appears, but non-banner regions get no flash.
 */
const fallbackBannerData: ConsentBannerData = {
  showBanner: true,
  layout: 'bottom',
  backgroundColor: '#ffffff',
  title: 'Privacy Preference Center',
  description: 'By selecting "Accept Cookies," you consent to the use of cookies on your device, which improve site navigation, analyze usage patterns, and support our marketing initiatives.',
  acceptAllButton: {
    text: 'Accept Cookies',
    color: '#1a7b37',
    textColor: '#ffffff'
  },
  rejectAllButton: {
    text: 'Reject All',
    color: '#1a7b37',
    textColor: '#ffffff'
  },
  showPreferencesButton: {
    text: 'Cookie Settings',
    color: '#ffffff',
    textColor: '#1a7b37'
  },
  policyLinkText: 'Privacy Policy',
  policyLinkUrl: oneTrustConfig.policyUrl,
  defaultGroupConsents: {}
};

/**
 * Props for the CachedConsentBanner.
 * The renderBanner prop allows apps to pass their own styled UI component,
 * avoiding a circular dependency between tracking and ark-skippy.
 */
export interface CachedConsentBannerProps {
  testMode: boolean;
  renderBanner: React.FC<ConsentBannerUIProps>;
}

/**
 * Renders the consent banner with a static fallback in the server output
 * (so it's part of the LCP paint), then replaces with region-specific data
 * fetched from `/api/consent/banner-data` on mount.
 *
 * The route handler is the only place that calls `headers()`, which keeps
 * dynamic-rendering classification out of the page render path. That's
 * required for `notFound()` to render cleanly under cacheComponents.
 *
 * Architecture note: a previous version awaited `headers()` here directly
 * to render region-specific data on the server. Under cacheComponents
 * without PPR, that opted the entire layout into dynamic rendering and
 * cascaded into "Invalid revalidate configuration provided: 0 < 1" on
 * 404 paths. The route-handler split keeps the LCP win without the taint.
 */
export const CachedConsentBanner: React.FC<CachedConsentBannerProps> = t0 => {
  const $ = _c(7);
  const {
    testMode,
    renderBanner
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getFeatureFlags();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    disableThirdPartyScripts
  } = t1;
  const [bannerData, setBannerData] = useState(fallbackBannerData);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (disableThirdPartyScripts) {
        return;
      }
      const controller = new AbortController();
      fetch("/api/consent/banner-data", {
        signal: controller.signal
      }).then(_temp).then(payload => {
        const next = (payload as {
          bannerData?: ConsentBannerData;
        } | null)?.bannerData;
        if (next) {
          setBannerData(next);
        }
      }).catch(_temp2);
      return () => {
        controller.abort();
      };
    };
    t3 = [disableThirdPartyScripts];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  if (disableThirdPartyScripts) {
    return null;
  }
  let t4;
  if ($[3] !== bannerData || $[4] !== renderBanner || $[5] !== testMode) {
    t4 = <ConsentBanner bannerData={bannerData} testMode={testMode} renderBanner={renderBanner} />;
    $[3] = bannerData;
    $[4] = renderBanner;
    $[5] = testMode;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
function _temp(response) {
  return response.ok ? response.json() : null;
}
function _temp2(error) {
  if (error instanceof DOMException && error.name === "AbortError") {
    return;
  }
  console.error("[consent-banner] Failed to fetch region-specific banner data:", error);
}